import { Box, Button, Flex, Spinner, Stack, Text } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query";
import type { TFunction } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  SavingsList,
  useGetContractSavingsQuery,
  type ContractSavingDto,
  type ContractSavingsDto,
  type SavingsFigureDto,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { ListLoadError } from "../../common/ListLoadError";
import { Paging } from "../../common/Paging";
import { useApiError } from "../../common/errors/useApiError";
import { displaySum } from "../../common/formatting/displaySum";
import { formatNumber } from "../../common/formatting/formatNumber";
import { urls } from "../../urls";
import { MutedCell, PanelRow, PanelTable, TitleCell } from "../home/panels/PanelTable";
import { contractScopeText } from "./contractFigures";
import { LargestSavings } from "./LargestSavings";
import { OverviewFigureGrid, OverviewFigureTile } from "./OverviewFigureTile";
import { IncompletePeriod, OverviewPeriodChoice, useOverviewPeriod } from "./OverviewPeriodChoice";
import {
  LIST_PARAM,
  SAVINGS_ORDER,
  formatShare,
  missingBaselineLabel,
  parseSavingsList,
  savingsDefinition,
  savingsLabel,
} from "./savingsFigures";
import { WithoutSavings } from "./WithoutSavings";

const LISTED_CONTRACTS = 10;

const COUNTED_ONCE_PER_PROCUREMENT: SavingsList[] = [SavingsList.BelowEstimate, SavingsList.AboveEstimate];

type SavingsQuery = { orgId: string; from: string; to: string };

export const SavingsOverview = () => {
  const organizationId = useLoggedInWithOrgContextState().selectedOrg.id;
  const periodState = useOverviewPeriod();
  const { range } = periodState;
  const query = range ? { orgId: organizationId, from: range.from, to: range.to } : null;
  const { data, isLoading, isFetching, error, refetch } = useGetContractSavingsQuery(query ?? skipToken);
  const { message } = useApiError();

  return (
    <Stack spacing="5">
      <Flex gap="3" wrap="wrap" align="center">
        <OverviewPeriodChoice state={periodState} />
        {isFetching && !isLoading && <Spinner size="sm" color="smPrimary" />}
      </Flex>
      {!range ? (
        <IncompletePeriod />
      ) : isLoading ? (
        <Spinner color="smPrimary" />
      ) : error || !data || !query ? (
        <ListLoadError message={message(error)} onRetry={() => void refetch()} isRetrying={isFetching} />
      ) : (
        <Savings savings={data} query={query} />
      )}
    </Stack>
  );
};

const Savings = ({ savings, query }: { savings: ContractSavingsDto; query: SavingsQuery }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const opened = parseSavingsList(searchParams.get(LIST_PARAM));
  const byList = new Map(savings.figures.map((figure) => [figure.list, figure]));
  const contractsIn = (list: SavingsList) => byList.get(list)?.contracts ?? 0;
  const withoutBaseline = contractsIn(SavingsList.WithoutBaseline);
  const linkTo = (list: SavingsList) => {
    const params = new URLSearchParams(searchParams);
    params.set(LIST_PARAM, list);
    return `?${params}`;
  };
  const closeList = () =>
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        params.delete(LIST_PARAM);
        return params;
      },
      { replace: true },
    );

  const noContracts = savings.figures.every((figure) => figure.contracts === 0);

  return (
    <Stack spacing="4">
      {noContracts ? (
        <Text fontSize="sm" color="smMuted" data-testid="savings-empty">
          {t("No contract was awarded or registered in this period.")}
        </Text>
      ) : (
        <OverviewFigureGrid columns={{ base: 1, sm: 2, lg: 3 }}>
          {SAVINGS_ORDER.map((list) => {
            const figure = byList.get(list);
            return figure ? (
              <SavingsTile key={list} figure={figure} currency={savings.currency} link={linkTo(list)} />
            ) : null;
          })}
        </OverviewFigureGrid>
      )}
      <Stack spacing="1" fontSize="sm" color="smMuted">
        <Text>{t("Based on what you have access to: {{scope}}", { scope: contractScopeText(t, savings.scope) })}</Text>
        {withoutBaseline > 0 && (
          <Text data-testid="without-baseline-note">
            {t("No baseline: {{count}} contracts are not included", { count: withoutBaseline })}
          </Text>
        )}
      </Stack>
      {opened && opened !== SavingsList.WithoutSaving && !noContracts && (
        <SavingsContracts
          key={`${opened}-${query.from}-${query.to}`}
          list={opened}
          query={query}
          currency={savings.currency}
          onClose={closeList}
        />
      )}
      {!noContracts && (
        <>
          <LargestSavings savings={savings} />
          <WithoutSavings
            key={`${query.from}-${query.to}`}
            query={query}
            count={contractsIn(SavingsList.WithoutSaving)}
            anyMeasured={[SavingsList.BelowEstimate, SavingsList.Negotiation, SavingsList.WithoutSaving].some(
              (list) => contractsIn(list) > 0
            )}
            currency={savings.currency}
            opened={opened === SavingsList.WithoutSaving}
            onShown={closeList}
          />
        </>
      )}
    </Stack>
  );
};

const SavingsTile = ({ figure, currency, link }: { figure: SavingsFigureDto; currency: string; link: string }) => {
  const { t } = useTranslation();
  const { amount } = figure;
  const inMoney = amount != null;
  return (
    <OverviewFigureTile
      label={savingsLabel(t, figure.list)}
      shown={inMoney ? displaySum({ value: Math.round(amount), currency }) : formatNumber(figure.contracts)}
      below={
        <Text fontSize="xs" color="smMuted">
          {inMoney ? moneyBelow(t, figure) : t("{{count}} contracts", { count: figure.contracts })}
        </Text>
      }
      definition={savingsDefinition(t, figure.list)}
      details={
        <Text>
          {t("{{count}} procurements", { count: figure.procurements })}
          {" · "}
          {t("{{count}} contracts", { count: figure.contracts })}
        </Text>
      }
      link={link}
      testId={figure.list}
    />
  );
};

const moneyBelow = (t: TFunction, figure: SavingsFigureDto): string => {
  const procurements = t("{{count}} procurements", { count: figure.procurements });
  if (figure.share == null) return procurements;
  const share = formatShare(figure.share);
  return figure.list === SavingsList.Negotiation
    ? t("{{share}} of the first bids · {{procurements}}", { share, procurements })
    : t("{{share}} of estimated value · {{procurements}}", { share, procurements });
};

const SavingsContracts = ({
  list,
  query,
  currency,
  onClose,
}: {
  list: SavingsList;
  query: SavingsQuery;
  currency: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [skip, setSkip] = useState(0);
  const { data, isLoading, isFetching, error, refetch } = useGetContractSavingsQuery({
    ...query,
    list,
    limit: LISTED_CONTRACTS,
    skip,
  });
  const { message } = useApiError();
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => {
    card.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, []);
  const listed = data?.listed;
  const columns = valueColumns(t, list, currency);

  return (
    <Stack
      ref={card}
      spacing="3"
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="lg"
      p="4"
      bg="smBackground"
      data-testid="savings-list"
    >
      <Flex justify="space-between" align="baseline" gap="3" wrap="wrap">
        <Text as="h2" fontWeight="semibold" color="smText">
          {listed
            ? t("{{label}} ({{count}} contracts)", { label: savingsLabel(t, list), count: listed.total })
            : savingsLabel(t, list)}
        </Text>
        <Button variant="link" size="sm" colorScheme="teal" onClick={onClose}>
          {t("Hide the list")}
        </Button>
      </Flex>
      {isLoading ? (
        <Spinner color="smPrimary" />
      ) : error || !listed ? (
        <ListLoadError message={message(error)} onRetry={() => void refetch()} isRetrying={isFetching} />
      ) : listed.total === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {t("No contracts in this list for the period.")}
        </Text>
      ) : (
        <>
          <Box overflowX="auto">
            <PanelTable
              headers={[String(t("Contract")), String(t("Procurement")), ...columns.map((column) => column.header)]}
            >
              {listed.contracts.map((contract) => (
                <PanelRow key={contract.contractId} to={rowLink(contract)}>
                  <TitleCell
                    title={contract.title || String(t("Untitled"))}
                    subtitle={contract.supplierName ?? undefined}
                  />
                  <MutedCell>{contract.procurementTitle ?? "—"}</MutedCell>
                  {columns.map((column) => (
                    <MutedCell key={column.header}>{column.value(contract)}</MutedCell>
                  ))}
                </PanelRow>
              ))}
            </PanelTable>
          </Box>
          {listed.total > LISTED_CONTRACTS && (
            <Paging
              limit={LISTED_CONTRACTS}
              skip={skip}
              setSkip={setSkip}
              isLoading={isFetching}
              count={listed.total}
            />
          )}
          {COUNTED_ONCE_PER_PROCUREMENT.includes(list) && (
            <Text fontSize="sm" color="smMuted" data-testid="counted-once-note">
              {t("A procurement with several contracts is counted once.")}
            </Text>
          )}
        </>
      )}
    </Stack>
  );
};

const rowLink = (contract: ContractSavingDto): string =>
  contract.procurementId
    ? urls.app.sourcingEvents.view.go(contract.procurementId)
    : urls.app.contracts.view.go(contract.contractId);

type ValueColumn = { header: string; value: (contract: ContractSavingDto) => string };

const valueColumns = (t: TFunction, list: SavingsList, currency: string): ValueColumn[] => {
  const money = (value?: number | null) => (value == null ? "—" : displaySum({ value: Math.round(value), currency }));
  const estimate: ValueColumn[] = [
    { header: String(t("Estimated value")), value: (contract) => money(contract.estimatedValue) },
    { header: String(t("Value at award")), value: (contract) => money(contract.awardedValue) },
  ];
  switch (list) {
    case SavingsList.BelowEstimate:
      return [...estimate, { header: String(t("Below estimated value")), value: (c) => money(c.estimateDifference) }];
    case SavingsList.AboveEstimate:
      return [
        ...estimate,
        {
          header: String(t("Above estimated value")),
          value: (c) => money(c.estimateDifference == null ? null : -c.estimateDifference),
        },
      ];
    case SavingsList.Negotiation:
      return [
        { header: String(t("First bid")), value: (c) => money(c.firstBid) },
        { header: String(t("Last round")), value: (c) => money(c.lastRound) },
        { header: String(t("Saved in negotiation")), value: (c) => money(c.savedInNegotiation) },
      ];
    case SavingsList.WithoutSaving:
      return [...estimate, { header: String(t("Saved in negotiation")), value: (c) => money(c.savedInNegotiation) }];
    case SavingsList.WithoutBaseline:
      return [
        {
          header: String(t("Cause")),
          value: (c) => (c.missingBaseline ? missingBaselineLabel(t, c.missingBaseline) : "—"),
        },
      ];
  }
};
